<template>
	<UserPageWrapper :accountSettings="true">
		<Title>{{ $t("seo.pages.notifications.title") }}</Title>
		<Meta name="description" :content="$t('seo.pages.notifications.description')" />
		<Link rel="canonical" :href="`https://${globalConfig.host}${$route.fullPath}`" />
		<Meta property="og:url" :content="`https://${globalConfig.host}${$route.fullPath}`" />
		<Meta property="og:type" content="website" />
		<Meta
			property="og:locale"
			:content="`${$i18n.locale}_${$i18n.locale == 'en' ? 'US' : $i18n.locale.toUpperCase()}`"
		/>
		<Meta property="og:title" :content="$t('seo.pages.notifications.title')" />
		<Meta property="og:description" :content="$t('seo.pages.notifications.description')" />
		<Meta
			property="og:image"
			content="https://res.cloudinary.com/djbs/image/upload/v1669288084/content/DreamJobs/Graphics/ogimage_dreamjobs.png"
		/>
		<Meta property="og:image:type" content="image/jpeg" />
		<Meta property="og:image:width" content="1200" />
		<Meta property="og:image:height" content="628" />

		<DjBox class="bg-gray-100 md:bg-white border-0 md:border pt-5 md:pt-8 px-5 md:px-10 pb-5 md:pb-10 md:mb-4">
			<BaseHeading type="h2" class="leading-3 text-center md:text-left mb-6">
				<span class="text-2xl">{{ $t("user.notifications.title") }}</span>
			</BaseHeading>

			<DjBox class="notifications-djbox flex justify-between pt-5 sm:pt-7 pl-4 sm:pl-8 pr-0 sm:pr-4 pb-5 sm:pb-7">
				<label class="flex font-medium items-center">{{ $t("user.notifications.systemMessages") }}</label>
				<BaseSwitch v-model="system" disabled></BaseSwitch>
			</DjBox>

			<template v-if="!globalConfig.isBlue">
				<BaseHeading type="h3" class="leading-3 text-center md:text-left mt-8">
					<span class="text-xl">{{ $t("newsletter.newsletter") }}</span>
				</BaseHeading>
				<p class="mb-3">{{ $t("newsletter.newsletterCta") }}</p>

				<DjBox
					class="notifications-djbox flex justify-between pt-5 sm:pt-7 pl-4 sm:pl-8 pr-0 sm:pr-4 pb-5 sm:pb-7 sm:mb-4"
				>
					<label class="flex font-medium items-center">{{ $t("user.notifications.newsletterIT") }}</label>
					<BaseSwitch @click="clearErrors()" v-model="newsletterIT"></BaseSwitch>
				</DjBox>

				<DjBox
					class="notifications-djbox flex justify-between pt-5 sm:pt-7 pl-4 sm:pl-8 pr-0 sm:pr-4 pb-5 sm:pb-7 sm:mb-4"
				>
					<label class="flex font-medium items-center">{{ $t("user.notifications.newsletterOffice") }}</label>
					<BaseSwitch @click="clearErrors()" v-model="newsletterOffice"></BaseSwitch>
				</DjBox>

				<DjBox
					v-if="szmdStarted"
					class="notifications-djbox flex justify-between pt-5 sm:pt-7 pl-4 sm:pl-8 pr-0 sm:pr-4 pb-5 sm:pb-7 sm:mb-4"
				>
					<label class="flex font-medium items-center">{{ $t("user.notifications.newsletterSzmd") }}</label>
					<BaseSwitch @click="clearErrors()" v-model="newsletterSzmd"></BaseSwitch>
				</DjBox>
			</template>

			<BaseHeading type="h3" class="leading-3 text-center md:text-left mt-8">
				<span class="text-xl">{{ $t("user.notifications.jobsShort") }}</span>
			</BaseHeading>
			<p class="mb-3">{{ $t("user.notifications.jobsCta") }}</p>

			<DjBox
				class="notifications-djbox flex justify-between pt-5 sm:pt-7 pl-4 sm:pl-8 pr-0 sm:pr-4 pb-5 sm:pb-7 sm:mb-4"
			>
				<label class="flex font-medium items-center">{{ $t("user.notifications.jobs") }}</label>
				<BaseSwitch @click="clearErrors()" v-model="jobNotifications"></BaseSwitch>
			</DjBox>
			<NuxtLink class="text-dj-blue" :href="`/${$i18n.locale}/user/account#notificationsModify`">
				{{ $t("user.notifications.modify") }}
			</NuxtLink>

			<p v-if="serverErrorMsg.length > 0" class="text-dj-red text-sm mt-2">{{ serverErrorMsg }}</p>

			<div class="flex justify-end mt-8">
				<BaseButton @click="save" class="min-w-full sm:min-w-min px-12">{{ $t("user.save") }}</BaseButton>
			</div>
		</DjBox>
	</UserPageWrapper>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";

export default defineComponent({
	name: "UserNotificationsPage",
	setup() {
		definePageMeta({
			middleware: ["auth"],
		});
		const { locale, t } = useI18n({
			useScope: "global",
		});

		const app = useNuxtApp();
		const gtm = useGTM();

		const region = ref("HU");
		const audienceList = ref(["B2C_IT", "B2C_OFFICE", "SZMD_CONF"]);

		const system = ref(true);
		const newsletterIT = ref(false);
		const newsletterOffice = ref(false);
		const newsletterSzmd = ref(false);
		const jobNotifications = ref(false);
		const fetchedSubStatus = ref([]);
		const fetchedNotificationStatus = ref(false);

		const serverErrorMsg = ref("");

		const config = useRuntimeConfig();

		const isAuth = useState("isAuth");

		const email = ref("");

		const szmdEntryStartAt = new Date(config.public.activeConfig.szmdConfig.entryStartAt).getTime();
		const now = new Date().getTime();
		const szmdStarted = szmdEntryStartAt < now;

		function clearErrors() {
			serverErrorMsg.value.length > 0 ? (serverErrorMsg.value = "") : null;
		}

		function save() {
			const toSubscribe = [];
			const toUnSubscribe = [];
			newsletterIT.value == true
				? toSubscribe.push(audienceList.value[0])
				: toUnSubscribe.push(audienceList.value[0]);
			newsletterOffice.value == true
				? toSubscribe.push(audienceList.value[1])
				: toUnSubscribe.push(audienceList.value[1]);
			newsletterSzmd.value == true
				? toSubscribe.push(audienceList.value[2])
				: toUnSubscribe.push(audienceList.value[2]);

			clearErrors();

			// Prevent Minicrm actions if user not changed settings
			if (!globalConfig.isBlue) {
				if (
					arrayEquals(fetchedSubStatus.value, [
						newsletterIT.value,
						newsletterOffice.value,
						newsletterSzmd.value,
					]) == false
				) {
					subscribe(toSubscribe);
				}
			}

			if (fetchedNotificationStatus.value != jobNotifications.value) {
				setJobNotifications();
			}
		}

		async function subscribe(arr: unknown) {
			await $fetch(`/api/minicrm-subscribe`, {
				method: "POST",
				body: {
					xsrf: useAuth.getXsrfToken(),
					token: useCookie("token").value,
					operation: "subscribe-to-minicrm",
					email: email.value,
					// Object keys must match with env variable names
					new_letters_subscribe: {
						it: arr.includes("B2C_IT"),
						office: arr.includes("B2C_OFFICE"),
						szmd: arr.includes("SZMD_CONF"),
					},
				},
			})
				.then(() => {
					useAuth.updateUser().then(() => {
						const userData = useAuth.getUser();
						newsletterIT.value = userData.minicrm_subscribes.it;
						newsletterOffice.value = userData.minicrm_subscribes.office;
						newsletterSzmd.value = userData.minicrm_subscribes.szmd;
					});

					try {
						gtm.trackEvent({ event: "b2c-newsletter" });
						app.$openToast({ message: t("user.successfulSave"), type: "success", description: "" });
					} catch (error) {
						console.error(error);
					}
				})
				.catch((error) => {
					console.error("user notify", error);

					try {
						app.$openToast({
							message: t("errors.somethingWentWrong"),
							type: "error",
							description: error.response,
						});
					} catch (error) {
						console.error(error);
					}
				});
		}

		function setJobNotifications() {
			$fetch(`/api/update-profile`, {
				// API for all profile update
				method: "POST",
				body: {
					subscribe: jobNotifications.value ? 1 : 0,
					xsrf: useAuth.getXsrfToken(),
					token: useCookie("token").value,
					operation: "subscribe",
					locale: locale.value,
					region: region.value,
				},
			})
				.then(() => {
					updateUser();
					try {
						if (jobNotifications.value) {
							// GTM tracking
							gtm.trackEvent({ event: "b2c-jobnotification-signup-registereduser" });
						} else {
							// GTM tracking
							gtm.trackEvent({ event: "b2c-jobnotification-unsubscribe-registereduser" });
						}

						app.$openToast({ message: t("user.successfulSave"), type: "success", description: "" });
					} catch (error) {
						console.error(error);
					}
				})
				.catch((error) => {
					if (error.response && error.response._data && error.response._data.message) {
						serverErrorMsg.value = error.statusText || error.response._data.message || "";
					}
				});
		}

		function updateUser() {
			useAuth.updateUser().then(() => {
				getAccountData();
			});
		}

		function getAccountData() {
			const userData = useAuth.getUser();
			email.value = userData.email;
			fetchedNotificationStatus.value = userData.newsletter_recommended_jobs == 1 ? true : false;
			jobNotifications.value = userData.newsletter_recommended_jobs == 1 ? true : false;

			newsletterIT.value = userData.minicrm_subscribes.it == 1 ? true : false;
			newsletterOffice.value = userData.minicrm_subscribes.office == 1 ? true : false;
			newsletterSzmd.value = userData.minicrm_subscribes.szmd == 1 ? true : false;
		}

		function arrayEquals(a, b) {
			return (
				Array.isArray(a) &&
				Array.isArray(b) &&
				a.length === b.length &&
				a.every((val, index) => val === b[index])
			);
		}

		watch(isAuth, (val: boolean) => {
			val == true && getAccountData();
		});

		onMounted(() => {
			region.value = useRegion.getCurrentRegion().code;
			isAuth.value == true && getAccountData();
		});

		return {
			system,
			newsletterIT,
			newsletterOffice,
			newsletterSzmd,
			jobNotifications,
			save,
			email,
			szmdStarted,
			serverErrorMsg,
			clearErrors,
		};
	},
});
</script>
<style lang="scss" scoped>
.notifications-djbox {
	margin-bottom: 1rem;
}
</style>
